import { Observable, type ReadonlyObservable } from "@/lib/studio/observable";
import {
  DEFAULT_CHORD_NOTATION,
  DEFAULT_NOTE_NOTATION,
  type NoteNotation,
} from "./notation";

const NOTE_NOTATION_LS_KEY = "virtuosa.noteNotation";
const CHORD_NOTATION_LS_KEY = "virtuosa.chordNotation";
const NOTE_NAMES_LS_KEY = "virtuosa.noteNamesOnScore";

/**
 * Les noms de touches sont-ils écrits sur la partition ?
 *
 * Allumé par défaut, et c'est le bon défaut : la portée grave désormais sa clé
 * et ses altérations, donc elle se lit seule — mais quelqu'un qui ne lit pas
 * encore a besoin des noms, et il est le plus nombreux. L'éteindre est ce qui
 * fait de la portée une partition ordinaire, ce que demande quelqu'un qui lit
 * déjà, ou un professeur qui veut que son élève apprenne à lire.
 */
export const DEFAULT_NOTE_NAMES_ON_SCORE = true;

function isNoteNotation(raw: string | null): raw is NoteNotation {
  return raw === "english" || raw === "latin";
}

function readPersisted(key: string, fallback: NoteNotation): NoteNotation {
  if (typeof window === "undefined") {
    return fallback;
  }

  const raw = window.localStorage.getItem(key);

  if (isNoteNotation(raw)) {
    return raw;
  }

  return fallback;
}

function readPersistedFlag(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") {
    return fallback;
  }

  const raw = window.localStorage.getItem(key);

  return raw === null ? fallback : raw === "1";
}

function persistFlag(key: string, value: boolean): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(key, value ? "1" : "0");
}

function persist(key: string, value: NoteNotation): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(key, value);
}

/**
 * Reactive holder of the note-name notation preference (Do Ré Mi vs C D E).
 *
 * Two layers, same spirit as `PerfProfileStore`:
 * - **preference** : what the user picked in the settings, persisted as-is.
 * - **effective**  : what the UI actually renders. Latin chord symbols only
 *   make sense alongside latin note names — `Do maj7` next to a keyboard
 *   labelled `C` reads as a bug — so english note names force english chord
 *   symbols, without destroying the user's chord preference underneath.
 *
 * Deliberately NOT part of `Studio`: that object is built inside an effect
 * (so it is null on first render, which would flash every label from `C` to
 * `Do` at boot) and it drags in the AudioContext, which a purely textual
 * preference has no business depending on. Living in a module-level store
 * also lets the Pixi layer read and subscribe outside React.
 */
export class NotationStore {
  private readonly note: Observable<NoteNotation>;
  private readonly chordPreference: Observable<NoteNotation>;
  private readonly chordEffective: Observable<NoteNotation>;
  private readonly noteNames: Observable<boolean>;

  constructor() {
    this.note = new Observable<NoteNotation>(
      readPersisted(NOTE_NOTATION_LS_KEY, DEFAULT_NOTE_NOTATION),
      { onChange: (v) => persist(NOTE_NOTATION_LS_KEY, v) },
    );

    this.chordPreference = new Observable<NoteNotation>(
      readPersisted(CHORD_NOTATION_LS_KEY, DEFAULT_CHORD_NOTATION),
      { onChange: (v) => persist(CHORD_NOTATION_LS_KEY, v) },
    );

    this.chordEffective = new Observable<NoteNotation>(this.resolveChord());

    this.noteNames = new Observable<boolean>(
      readPersistedFlag(NOTE_NAMES_LS_KEY, DEFAULT_NOTE_NAMES_ON_SCORE),
      { onChange: (v) => persistFlag(NOTE_NAMES_LS_KEY, v) },
    );
  }

  private resolveChord(): NoteNotation {
    if (this.note.value === "english") {
      return "english";
    }

    return this.chordPreference.value;
  }

  /** Notation used for bare note names. */
  get noteNotation(): ReadonlyObservable<NoteNotation> {
    return this.note;
  }

  /** Notation actually used for chord symbols, after the coherence rule. */
  get chordNotation(): ReadonlyObservable<NoteNotation> {
    return this.chordEffective;
  }

  /** Raw chord preference, for the settings UI to reflect what was picked
   *  even while the coherence rule overrides it. */
  get chordNotationPreference(): ReadonlyObservable<NoteNotation> {
    return this.chordPreference;
  }

  /** Les noms de touches écrits sur la portée. */
  get noteNamesOnScore(): ReadonlyObservable<boolean> {
    return this.noteNames;
  }

  setNoteNamesOnScore(next: boolean): void {
    this.noteNames.set(next);
  }

  setNoteNotation(next: NoteNotation): void {
    this.note.set(next);
    this.chordEffective.set(this.resolveChord());
  }

  setChordNotation(next: NoteNotation): void {
    this.chordPreference.set(next);
    this.chordEffective.set(this.resolveChord());
  }

  /** Fires whenever anything that affects rendering changes. Used by the
   *  non-React consumers (the piano-roll canvas) to invalidate their labels. */
  subscribe(listener: () => void): () => void {
    const offNote = this.note.subscribe(() => listener());
    const offChord = this.chordEffective.subscribe(() => listener());

    return () => {
      offNote();
      offChord();
    };
  }
}

let storeInstance: NotationStore | null = null;

export function getNotationStore(): NotationStore {
  if (!storeInstance) {
    storeInstance = new NotationStore();
  }

  return storeInstance;
}

/** Test/dev hook to drop the singleton store between runs. */
export function clearNotationStoreCache(): void {
  storeInstance = null;
}
